import {
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';

interface CodeItem {
  code: string | number;
  value: string;
}

interface NPSDVehicleDetailsProps {
  vehicle: any;
  isReadOnly?: boolean;
  states?: CodeItem[];
  vehicleTypes?: CodeItem[];
  onChange?: (vehicle: any) => void;
}

const PLATE_COUNTRY_OPTIONS: CodeItem[] = [
  { code: '', value: '' },
  { code: 'US', value: 'United States' },
  { code: 'CA', value: 'Canada' },
  { code: 'OTHER', value: 'Other' },
];

const CANADIAN_PROVINCES: CodeItem[] = [
  { code: '', value: '' },
  { code: 'AB', value: 'Alberta' },
  { code: 'BC', value: 'British Columbia' },
  { code: 'MB', value: 'Manitoba' },
  { code: 'NB', value: 'New Brunswick' },
  { code: 'NL', value: 'Newfoundland and Labrador' },
  { code: 'NS', value: 'Nova Scotia' },
  { code: 'NT', value: 'Northwest Territories' },
  { code: 'NU', value: 'Nunavut' },
  { code: 'ON', value: 'Ontario' },
  { code: 'PE', value: 'Prince Edward Island' },
  { code: 'QC', value: 'Quebec' },
  { code: 'SK', value: 'Saskatchewan' },
  { code: 'YT', value: 'Yukon' },
];

export function NPSDVehicleDetails({
  vehicle,
  isReadOnly = false,
  states = [],
  vehicleTypes = [],
  onChange,
}: NPSDVehicleDetailsProps) {
  const { theme } = useTheme();

  const [plateIssuedPickerOpen, setPlateIssuedPickerOpen] = useState(false);
  const [plateExpiresPickerOpen, setPlateExpiresPickerOpen] = useState(false);

  // Field update helper
  const updateField = useCallback((field: string, value: any) => {
    const updated = { ...vehicle, [field]: value };
    onChange?.(updated);
  }, [vehicle, onChange]);

  // Computed: filteredStatesProvinces
  const filteredStatesProvinces = useMemo((): CodeItem[] => {
    const country = vehicle.plateCountry;
    if (country === 'CA') {
      return CANADIAN_PROVINCES;
    }
    return states;
  }, [vehicle.plateCountry, states]);

  // Computed: stateProvinceLabel
  const stateProvinceLabel = useMemo((): string => {
    const country = vehicle.plateCountry;
    if (country === 'CA') return 'Province';
    if (country === 'US') return 'State';
    return 'State/Province';
  }, [vehicle.plateCountry]);

  // Computed: isOtherCountry
  const isOtherCountry = useMemo((): boolean => {
    return vehicle.plateCountry === 'OTHER';
  }, [vehicle.plateCountry]);

  // Date handlers
  const handlePlateIssuedChange = useCallback((value: Date | null) => {
    updateField('plateIssued', value);
  }, [updateField]);

  const handlePlateExpiresChange = useCallback((value: Date | null) => {
    updateField('plateExpires', value);
  }, [updateField]);

  // State change handler
  const handleStateChange = useCallback((val: string | number | null) => {
    const stateCode = val ?? '';
    const country = vehicle.plateCountry;
    // If no country is selected and a US state is chosen, auto-select US
    if (!country && stateCode) {
      const updated = { ...vehicle, plateState: stateCode, plateCountry: 'US' };
      onChange?.(updated);
    } else {
      updateField('plateState', stateCode);
    }
  }, [vehicle, onChange, updateField]);

  // Vehicle type change handler
  const handleVehicleTypeChange = useCallback((val: string | number | null) => {
    updateField('vehicleType', val);
  }, [updateField]);

  // Plate country change handler
  const handlePlateCountryChange = useCallback((val: string | number | null) => {
    const newCountry = val ?? '';
    const oldCountry = vehicle.plateCountry;
    if (oldCountry !== newCountry) {
      const updated = { ...vehicle, plateCountry: newCountry, plateState: '' };
      onChange?.(updated);
    } else {
      updateField('plateCountry', newCountry);
    }
  }, [vehicle, onChange, updateField]);

  // Clear date handlers
  const clearPlateIssued = useCallback(() => {
    updateField('plateIssued', null);
    setPlateIssuedPickerOpen(false);
  }, [updateField]);

  const clearPlateExpires = useCallback(() => {
    updateField('plateExpires', null);
    setPlateExpiresPickerOpen(false);
  }, [updateField]);

  // Display helpers
  const getStateDisplay = useCallback((stateCode: string): string => {
    if (!stateCode) return '';
    const stateInFiltered = filteredStatesProvinces.find((s) => s.code === stateCode);
    if (stateInFiltered) return stateInFiltered.value;
    const state = states.find((s) => s.code === stateCode);
    if (state) return state.value;
    const province = CANADIAN_PROVINCES.find((p) => p.code === stateCode);
    return province ? province.value : stateCode;
  }, [filteredStatesProvinces, states]);

  const getCountryDisplay = useCallback((countryCode: string): string => {
    if (!countryCode) return '';
    const country = PLATE_COUNTRY_OPTIONS.find((c) => c.code === countryCode);
    return country ? country.value : countryCode;
  }, []);

  const getVehicleTypeDisplay = useCallback((typeCode: number | null): string => {
    if (typeCode === null || typeCode === undefined) return '';
    const vType = vehicleTypes.find((t) => t.code === typeCode);
    return vType ? vType.value : '';
  }, [vehicleTypes]);

  const formatDate = useCallback((date: any): string => {
    if (!date) return '';
    const m = (window as any).moment.utc(date);
    if (!m.isValid()) return '';
    return m.format('DD MMM YYYY');
  }, []);

  return (
    <div className={`npsd-vehicle-details npsd-entity-details${theme ? ' ' + theme : ''}`}>
      <div className="details-layout">
        {/* Main Column (left) */}
        <div className="details-column-main">
          {/* Plate / VIN Section */}
          <div className="panel plate-panel">
            <div className="panel-header">PLATE / VIN</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label required">License Plate</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.plateNumber || ''}
                    onChange={(e) => updateField('plateNumber', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Plate Country</label>
                  {!isReadOnly ? (
                    <Dropdownlist
                      value={vehicle.plateCountry}
                      items={PLATE_COUNTRY_OPTIONS}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select Country"
                      onChange={handlePlateCountryChange}
                    />
                  ) : (
                    <input
                      type="text"
                      className="kstc-input"
                      value={getCountryDisplay(vehicle.plateCountry)}
                      disabled
                    />
                  )}
                </div>
              </div>

              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Plate {stateProvinceLabel}</label>
                  {isOtherCountry && !isReadOnly ? (
                    <input
                      type="text"
                      className="kstc-input"
                      value={vehicle.plateState || ''}
                      onChange={(e) => updateField('plateState', e.target.value)}
                      placeholder="Enter state/province"
                    />
                  ) : !isReadOnly ? (
                    <Dropdownlist
                      value={vehicle.plateState}
                      items={filteredStatesProvinces}
                      labelKey="value"
                      valueKey="code"
                      placeholder={'Select ' + stateProvinceLabel}
                      onChange={handleStateChange}
                    />
                  ) : (
                    <input
                      type="text"
                      className="kstc-input"
                      value={getStateDisplay(vehicle.plateState)}
                      disabled
                    />
                  )}
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Temp Plate</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.temporaryPlate || ''}
                    onChange={(e) => updateField('temporaryPlate', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Plate Issued</label>
                  {!isReadOnly ? (
                    <div className="datetime-field">
                      <DateTimePicker
                        value={vehicle.plateIssued ? new Date(vehicle.plateIssued) : null}
                        type="date"
                        clearable={false}
                        appendToBody={true}
                        popupClass={theme}
                        open={plateIssuedPickerOpen}
                        onChange={handlePlateIssuedChange}
                        onOpenChange={(open) => { if (!open) setPlateIssuedPickerOpen(false); }}
                      />
                      {vehicle.plateIssued && (
                        <button className="btn-clear-date" onClick={clearPlateIssued} title="Clear">
                          <i className="fas fa-times-circle"></i>
                        </button>
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="kstc-input"
                      value={formatDate(vehicle.plateIssued)}
                      disabled
                    />
                  )}
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Plate Expires</label>
                  {!isReadOnly ? (
                    <div className="datetime-field">
                      <DateTimePicker
                        value={vehicle.plateExpires ? new Date(vehicle.plateExpires) : null}
                        type="date"
                        clearable={false}
                        appendToBody={true}
                        popupClass={theme}
                        open={plateExpiresPickerOpen}
                        onChange={handlePlateExpiresChange}
                        onOpenChange={(open) => { if (!open) setPlateExpiresPickerOpen(false); }}
                      />
                      {vehicle.plateExpires && (
                        <button className="btn-clear-date" onClick={clearPlateExpires} title="Clear">
                          <i className="fas fa-times-circle"></i>
                        </button>
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="kstc-input"
                      value={formatDate(vehicle.plateExpires)}
                      disabled
                    />
                  )}
                </div>
              </div>

              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">VIN</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.vin || ''}
                    onChange={(e) => updateField('vin', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="panel description-panel">
            <div className="panel-header">DESCRIPTION</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label required">Make</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.make || ''}
                    onChange={(e) => updateField('make', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label required">Model</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.model || ''}
                    onChange={(e) => updateField('model', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Year</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.year || ''}
                    onChange={(e) => updateField('year', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Color</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.color || ''}
                    onChange={(e) => updateField('color', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Body Style</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.bodyStyle || ''}
                    onChange={(e) => updateField('bodyStyle', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Vehicle Type</label>
                  {!isReadOnly ? (
                    <Dropdownlist
                      value={vehicle.vehicleType}
                      items={vehicleTypes}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select Type"
                      onChange={handleVehicleTypeChange}
                    />
                  ) : (
                    <input
                      type="text"
                      className="kstc-input"
                      value={getVehicleTypeDisplay(vehicle.vehicleType)}
                      disabled
                    />
                  )}
                </div>
              </div>

              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Notes</label>
                  <textarea
                    className="form-textarea"
                    value={vehicle.notes || ''}
                    onChange={(e) => updateField('notes', e.target.value)}
                    disabled={isReadOnly}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Side Column (right) */}
        <div className="details-column-side">
          {/* Insurance Section */}
          <div className="panel insurance-panel">
            <div className="panel-header">INSURANCE</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Provider</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.insuranceCompanyName || ''}
                    onChange={(e) => updateField('insuranceCompanyName', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Policy #</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={vehicle.insurancePolicyNumber || ''}
                    onChange={(e) => updateField('insurancePolicyNumber', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDVehicleDetailsReact = NPSDVehicleDetails;
